import type { SendOutcome } from '../hooks/useSignaling';
import api from '../services/api';
import { audioRouting } from '../services/audio-routing.service';
import { noteCallDiag } from './callDiagGate';
import { callProgress, noteCallConnected } from './callProgress';
import type { PeerConnectionManager } from './PeerConnection';

/**
 * THE LIVE 1:1 CALL — owned by the CALL, not by the screen showing it.
 *
 * WHY THIS MODULE EXISTS. `PeerAudioRoom` used to hold the
 * `PeerConnectionManager`, the microphone and the remote `<audio>` sink in
 * component refs, and its unmount cleanup sent a `hangup` and called
 * `destroy()`. So every way OFF the call stage was also a way to end the call:
 * on a real phone on 2026-09-14, Android's Back hung up on the partner while
 * the floating pill — which reads store state the teardown never touched —
 * went on saying "In a call". Tapping "Return to call" remounted the room,
 * which built a SECOND manager and offered it to somebody who had already been
 * hung up on; the stage said "Connecting…" until the 30-second timeout.
 *
 * THE SPLIT THIS MODULE IS. A call has exactly two endings and they are
 * different functions:
 *
 *   `detachLiveCall` — the SCREEN went away. Nothing is torn down. The peer
 *     connection stays up, the microphone stays open, the partner's audio
 *     keeps playing through the record's own `<audio>` element, and outbound
 *     ICE and renegotiation keep flowing through the last `send` we were
 *     given. The next mount re-attaches to all of it.
 *
 *   `endLiveCall` — the PERSON ended the call. The partner is told, then the
 *     connection is destroyed and every device released. Idempotent, because
 *     the End button, the pill's End, the partner's hangup, a failed
 *     connection and the 30s timeout all arrive at the same funnel.
 *
 * ONE RECORD, not a map. A person is in at most one 1:1 call; keying by id
 * exists so a stale record can never be mistaken for the current call, not so
 * two can be held at once — `registerLiveCall` ends any record naming a
 * different session rather than leaving it to leak (class E: one copy of
 * "which call is up", and it is this one).
 *
 * THE NATIVE AUDIO SESSION IS PART OF THE CALL, for exactly the same reason
 * the microphone and the sink are. It used to be acquired and released by a
 * mount effect in `useInCallAudioSession`, which only the call STAGE mounted —
 * so walking off the stage dropped Android to `MODE_NORMAL` while the call went
 * on: measured on a phone 2026-09-15, after which the earpiece was unreachable
 * (there is no in-call route without an in-call mode) and the partner came out
 * of the loudspeaker for the rest of the conversation. It is taken in
 * `registerLiveCall` and handed back in `endLiveCall`, so its lifetime is the
 * call's and nothing else's; the hook is now a consumer that only reports when
 * it is up.
 *
 * WHAT IS NOT HERE. Inbound signalling: `useSignaling` is a hook and stops
 * polling when the stage unmounts, so a `hangup` the partner sends while you
 * are away waits on the server until you return. Two things notice in the
 * meantime, and BOTH end the call rather than only writing it down (see
 * `callEndingAction`): the connection state, which the record keeps following,
 * and the queue status poll the dashboard layout runs on every page — the
 * backend completes the session when the partner leaves, and the store's
 * session-ended branch reaches this module's `endLiveCall` from there.
 */

/** What became of it (useSignaling's SendOutcome): a value, never a rejection. */
export type LiveCallSend = (type: string, payload: object) => Promise<SendOutcome>;

export interface LiveCall {
  sessionId: number;
  pc: PeerConnectionManager;
  /**
   * The remote audio sink. It belongs to the record because the partner has to
   * keep being audible while the stage is gone — an `<audio>` element owned by
   * the component is an element that stops playing the moment you navigate.
   */
  audio: HTMLAudioElement | null;
  /** The microphone, acquired once per CALL rather than once per visit. */
  localStream: MediaStream | null;
  /** The last `send` an attached stage gave us — a plain HTTP POST that
   *  outlives the hook that made it, so a detached call can still emit. */
  send: LiveCallSend;
  /** The connection state as last reported, attached or not. */
  connectionState: string;
  /** Has the offer/answer exchange completed? Restored on re-attach so a
   *  renegotiation offer is not mistaken for the initial one. */
  handshakeComplete: boolean;
  /** The person's standing camera decision, so returning re-opens the camera
   *  they asked for. The DEVICE is closed on detach; only the intent survives,
   *  which is the same shape the lock-screen gate already has. */
  cameraOn: boolean;
}

let live: LiveCall | null = null;

/* -------------------------------------------------------------------------
 * WHEN A CONNECTION STATE IS AN ENDING — one answer, for both handlers.
 * ---------------------------------------------------------------------- */

/**
 * HOW LONG A LOST MEDIA PATH MAY STAY LOST BEFORE THE CALL IS OVER — the
 * reconnect window, measured from the moment the path went (`disconnected` or
 * `failed`), not from the latest state change.
 *
 * It was 3 s (`CALL_RECONNECT_BUDGET_MS`), and on staging on 2026-10-27 that
 * ended two calls the network was about to give back: a desktop's USB Wi-Fi
 * failed a TDLS direct-link setup ~10 s into video, ICE went `disconnected`,
 * and three seconds later the desktop sent `hangup`. A Wi-Fi hand-off, a
 * cellular blip or a driver abandoning a direct link takes longer than that.
 *
 * WHY 25 SECONDS. The floor is what a recovery needs end to end: the restart
 * grace below (4 s), a restart offer that may sit behind a signalling gap for
 * up to `SIGNAL_GAP_HOLD_MS` (10 s, #1088) on the partner's side, the answer's
 * trip back and the new generation's checks (~1-2 s) — about 16 s for the
 * FIRST attempt in the worst case, and 25 s leaves room for a second. The
 * ceiling is what nothing else contradicts: it stays under the stage's own
 * `CONNECT_TIMEOUT_MS` (30 s), so a reconnect is never given more patience
 * than a first connect; under Chrome's ICE consent timeout (~30 s to
 * `failed`), so the window is decided here and not by the browser; and far
 * under every server-side ending, none of which reads the media path at all —
 * the cap job (`EndEmpathySessionAtCapJob`, 90 min from the start), the stale
 * sweep (2 h), `SeekerNoShowJob` (only a seeker who never polled — an
 * established call has), and the signal cache (a restart offer waits two
 * minutes for its poll). Nothing ends the session sooner during a reconnect
 * except a person: a `hangup` or End.
 */
export const CALL_RECONNECT_WINDOW_MS = 25_000;

/**
 * How long a `disconnected` path is given to heal by itself before the offerer
 * restarts ICE. Short, because a restart costs one offer/answer and cannot hurt
 * a path that heals anyway; not zero, because most blips heal on their own
 * within a few consent checks and need no new ICE generation. `failed`
 * restarts at once — the browser has already said the old one is gone.
 */
export const CALL_RESTART_GRACE_MS = 4_000;

/**
 * Between restart attempts while the path is still down. A retry SUPERSEDES
 * NOTHING: if the last attempt's offer is still unanswered the manager
 * re-sends that same offer (`restartIce` → 'resent'), because its answer may
 * only be late — behind a 10 s signalling gap-hold, or on the server until a
 * partner who was off the stage polls again — and an answer must land on the
 * offer it answers (review of #1098: a replacing retry wedged the call). Only
 * a tick that finds the last offer ANSWERED and the path still down makes a
 * new ICE generation. Seven seconds is not derived from those holds — a retry
 * is safe at any spacing now — but from the window: three attempts fit it
 * from either entry (`failed`: 0, 7, 14 s; `disconnected`: 4, 11, 18 s), and
 * the last still has ~7 s for its checks before 25 s.
 */
export const CALL_RESTART_RETRY_MS = 7_000;

/** Restart attempts (ticks) per loss. Three fit the window from either entry. */
export const CALL_RESTART_MAX_ATTEMPTS = 3;

/**
 * From this NEW ICE GENERATION of a loss on (a re-sent offer is not one), a
 * restart FORCES THE TURN RELAY — when the call's TURN server has proved
 * itself by handing this connection a relay candidate (`canForceRelay`). The
 * first restart stays on every path, because most losses —
 * a Wi-Fi hand-off, a cellular blip — come back on the direct one and the
 * relay costs a round trip through the TURN server for the rest of the call.
 * The second exists for the loss the first cannot fix: on 2026-10-27 David
 * confirmed on device that the staging drops were a Samsung phone setting up
 * a Wi-Fi TDLS direct link to the PC, which breaks the L2 path between the
 * two LAN addresses. A plain restart re-checks the same host/prflx pairs over
 * that same broken link and may pick one again; a relayed pair goes
 * peer → TURN → peer and never touches it. So: plain at loss + 4 s (or at
 * `failed`), relay 7 s later if the plain one was answered and has not
 * brought the path back — all inside the 25 s window. The TURN
 * credential outlives any call (`SignalingService::CREDENTIAL_TTL_SECONDS`,
 * four hours and more against the 1:1's 90-minute cap).
 */
export const CALL_RESTART_RELAY_FROM_GENERATION = 2;

export type CallEndingAction = 'end' | 'wait' | 'keep';

/**
 * DOES THIS STATE END THE CALL — asked in exactly one place.
 *
 *   `closed` — only `close()` produces it, so somebody has already ended this.
 *   `failed` / `disconnected` — the path is lost: the reconnect window decides.
 *   everything else — nothing to end. (`connecting` DURING a loss is an ICE
 *     restart's checks and does not cancel the window; `followConnectionState`
 *     is what knows whether a loss is in progress.)
 */
export function callEndingAction(state: string): CallEndingAction {
  if (state === 'closed') return 'end';
  if (state === 'failed' || state === 'disconnected') return 'wait';
  return 'keep';
}

/**
 * WHO IS TOLD WHEN A CALL ENDED WITH NOBODY WATCHING.
 *
 * Only the detached funnel publishes here: when a screen is attached, the end
 * already reaches the flow through `onCallEnded`, and a second announcement
 * would be a second description of the same event. The empathy-queue store is
 * the subscriber — it owns the flags the pill and the flows read — and it
 * registers at module scope rather than being imported from here, because this
 * module is the one the store imports and a cycle between them is a hazard
 * neither of them needs.
 */
export type LiveCallEndedEvent = { sessionId: number; reason: string };
type LiveCallEndedListener = (event: LiveCallEndedEvent) => void;
const endedListeners = new Set<LiveCallEndedListener>();

export function onLiveCallEnded(listener: LiveCallEndedListener): () => void {
  endedListeners.add(listener);
  return () => endedListeners.delete(listener);
}

/* -------------------------------------------------------------------------
 * The call's native audio session.
 * ---------------------------------------------------------------------- */

/** Android's foreground-service notification for a 1:1 — the strings the hook
 *  used to pass, now owned where the session is. */
const CALL_NOTIFICATION = ['Empathy call', 'Call in progress'] as const;

let sessionHeld = false;

function newGate(): { ready: Promise<void>; open: () => void } {
  let open!: () => void;
  const ready = new Promise<void>((resolve) => {
    open = resolve;
  });

  return { ready, open };
}

/**
 * Opened once the session is ESTABLISHED. Replaced with a fresh, pending gate
 * every time the session is released, so a screen mounting for the NEXT call is
 * never handed the previous call's already-resolved promise — the 1:1's voice
 * gate waits on this before it creates the partner's playback stream, and
 * opening it early is the wrong-strategy bug that gate exists to prevent
 * (class E: one copy of "is the session up", and it is this one).
 */
let sessionGate = newGate();

/** For a consumer that wants to know WHEN the session is up. Never rejects: a
 *  failed session means "go ahead anyway", not "wait forever". */
export function callAudioSessionReady(): Promise<void> {
  return sessionGate.ready;
}

/** Is the call currently holding the native session? Exported for the tests
 *  that assert a screen leaving does not release it. */
export function isCallAudioSessionHeld(): boolean {
  return sessionHeld;
}

/**
 * Route to the loudspeaker, keep the screen awake, and keep audio alive when
 * the app is backgrounded or the screen is LOCKED (#8). Idempotent: a re-attach
 * finds the session already up and changes nothing. No-ops off native.
 */
function acquireCallAudioSession(): void {
  if (sessionHeld) return;
  sessionHeld = true;
  // AWAITED, and the only one here that is — this call IS the session coming
  // up. `Promise.resolve(...)` with `open` on BOTH arms because the one
  // outcome a waiter cannot recover from is a gate that never settles: it
  // would wait out the rest of the call with no error and nothing to retry.
  // A non-thenable return (a stub, a double, a refactor that drops `async`)
  // and a rejection both have to land here.
  void Promise.resolve(audioRouting.setSpeakerphoneOn(true)).then(
    () => sessionGate.open(),
    () => sessionGate.open(),
  );
  void audioRouting.setKeepScreenOn(true);
  void audioRouting.startBackgroundAudio(...CALL_NOTIFICATION);
}

/** Hand the session back. Only ever called from a path that actually ended a
 *  call, never from one that merely refused to. */
function releaseCallAudioSession(): void {
  if (!sessionHeld) return;
  sessionHeld = false;
  sessionGate = newGate();
  void audioRouting.stopBackgroundAudio();
  void audioRouting.setKeepScreenOn(false);
  void audioRouting.setSpeakerphoneOn(false);
}

/* -------------------------------------------------------------------------
 * THE RECONNECT WINDOW — one loss, one deadline, one owner.
 * ---------------------------------------------------------------------- */

/** Why a call gave up waiting for its path: `connection-<last state>`. */
export type GiveUp = (reason: string) => void;

/**
 * A LOSS IN PROGRESS, and there is at most one because there is one call.
 *
 * It belongs to the CALL, not to whichever handler saw the path go: the
 * deadline is armed once, at the first `disconnected`/`failed`, and walking
 * off the stage and back neither ends the call nor buys it a fresh window
 * (`detachLiveCall` and the stage's attach only RETARGET `giveUp` — the
 * ending that fits whoever is attached when the window runs out). It used to
 * be two timers, the stage's `disconnectTimerRef` and this module's
 * `reconnectWatch`, each restarted by every state change and each torn down
 * by a remount (class E: one copy of "how long has this call been lost").
 */
interface Outage {
  call: LiveCall;
  giveUp: GiveUp;
  deadline: ReturnType<typeof setTimeout>;
  restartTimer: ReturnType<typeof setTimeout> | null;
  /** Restart ticks spent, re-sends included — what the bound counts. */
  attempts: number;
  /** NEW ICE generations sent — what the relay escalation counts. */
  restarts: number;
  /** A restart fell due while no stage was attached: run it on re-attach. */
  due: boolean;
}

let outage: Outage | null = null;

/**
 * THE CALL WHOSE STAGE IS UP. Set when a screen builds a call
 * (`registerLiveCall` — only the stage builds one) or takes one over
 * (`attachReconnect`); cleared by `detachLiveCall` and by the call ending.
 * Restarts read it: inbound signalling is the stage's hook (`useSignaling`
 * polls only while it is mounted), so with no stage up nothing can collect a
 * restart's answer until the person returns.
 */
let attached: LiveCall | null = null;

function clearOutage(): void {
  if (!outage) return;
  clearTimeout(outage.deadline);
  if (outage.restartTimer) clearTimeout(outage.restartTimer);
  outage = null;
}

/**
 * WHAT THE STAGE SAYS WHILE A LOST PATH IS WAITED FOR, or null when none is.
 *
 *   'reconnecting' — this call HAD come up (utils/callProgress.ts, the one
 *                    record of that) and lost it: "Reconnecting…".
 *   'connecting'   — the first connect hit `failed`/`disconnected` before it
 *                    ever came up. Same window, same restarts — but there was
 *                    no connection to get back, and saying "Reconnecting…"
 *                    would describe a call the person never had (review of
 *                    #1098). The stage keeps "Connecting…" up instead.
 */
export function reconnectView(sessionId: number): 'reconnecting' | 'connecting' | null {
  if (!outage || outage.call !== live || outage.call.sessionId !== sessionId) return null;
  return callProgress(sessionId).connected ? 'reconnecting' : 'connecting';
}

/**
 * ONE ICE RESTART, from the offerer only.
 *
 * WHY ONE SIDE. The restart is an offer, and an offer from each side at once
 * is a glare: perfect negotiation would settle it (the impolite peer ignores
 * the polite one's, the polite one rolls back and answers), but every round of
 * it spends an attempt on both sides and an answer nobody needed. The impolite
 * peer — the initiator, who made the call's first offer — is the side whose
 * offer always wins a collision anyway, so it is the only one that starts one;
 * the polite side answers it through the ordinary `handleOffer` path. Before
 * this BOTH sides restarted on `failed` (`PeerConnectionManager`'s own
 * handler), and `failed` arrives ~30 s into a loss — after the call had long
 * been hung up.
 *
 * BOUNDED: `CALL_RESTART_MAX_ATTEMPTS` per loss, `CALL_RESTART_RETRY_MS` apart,
 * and never after the call is gone (class A: re-read at the moment of acting,
 * not when the timer was armed).
 *
 * ONLY WITH A STAGE UP. A restart that falls due while the person is off the
 * stage is held (`due`) and runs the moment a screen re-attaches
 * (`attachReconnect`) — on the loss's original deadline, not a fresh one. An
 * offer made with nobody to collect its answer is one more offer for the
 * answer to land on the wrong side of.
 */
function attemptRestart(o: Outage): void {
  o.restartTimer = null;
  if (outage !== o || live !== o.call) return;
  if (o.attempts >= CALL_RESTART_MAX_ATTEMPTS) return;
  if (attached !== o.call) {
    o.due = true;
    return;
  }
  o.due = false;
  o.attempts += 1;
  const attempt = o.attempts;
  const relayOnly =
    o.restarts >= CALL_RESTART_RELAY_FROM_GENERATION - 1 && o.call.pc.canForceRelay === true;
  const pc = o.call.pc;
  void Promise.resolve(pc.restartIce({ relayOnly })).then(
    (outcome) => {
      if (outcome === 'sent' && outage === o) o.restarts += 1;
      noteCallDiag('reconnect', {
        step: 'ice-restart',
        attempt,
        outcome,
        policy: pc.relayOnly ? 'relay' : 'all',
      });
    },
    () => {},
  );
  if (o.attempts < CALL_RESTART_MAX_ATTEMPTS) {
    o.restartTimer = setTimeout(() => attemptRestart(o), CALL_RESTART_RETRY_MS);
  }
}

/**
 * FOLLOW A CONNECTION STATE — the one implementation both handlers call: the
 * stage's (`PeerAudioRoom`) and the detached one below (class D: a call cannot
 * be given one window with a screen up and another without).
 *
 * Records the state on the call, then:
 *   `closed`                 → 'end'. The caller ends the call now.
 *   `connected`              → the loss, if any, is over: deadline and
 *                              restarts cancelled. 'keep'.
 *   `disconnected`/`failed`  → a loss: started if none is in progress (the
 *                              deadline is armed ONCE), restarts scheduled on
 *                              the offerer. 'wait'.
 *   `new`/`connecting`       → during a loss, an ICE restart's checks: the
 *                              window goes on ('wait'). Otherwise the first
 *                              connect, which is the stage's connect
 *                              timeout's business, not this one's ('keep').
 *
 * `giveUp` is how THIS caller ends a call whose window ran out; the latest
 * caller's wins, so the ending always fits whoever is attached by then.
 */
export function followConnectionState(
  call: LiveCall,
  state: string,
  giveUp: GiveUp,
): CallEndingAction {
  call.connectionState = state;
  // A call that came up — first time or after a loss — is one the ending
  // asks for a rating on (utils/callProgress.ts). Here, so the stage and the
  // detached handler cannot disagree about it (class D).
  if (state === 'connected') noteCallConnected(call.sessionId);
  const action = callEndingAction(state);
  // A record that is no longer the call has nothing left to wait for.
  if (live !== call) return action;
  if (action === 'end') {
    clearOutage();
    return 'end';
  }
  const inLoss = !!outage && outage.call === call;
  if (state === 'connected') {
    if (inLoss) noteRecovered(call, outage!);
    clearOutage();
    return 'keep';
  }
  if (action !== 'wait' && !inLoss) return 'keep';

  if (!inLoss) {
    clearOutage();
    const o: Outage = {
      call,
      giveUp,
      attempts: 0,
      restarts: 0,
      due: false,
      restartTimer: null,
      deadline: setTimeout(() => {
        // CLASS A — re-read: the call may have recovered, been ended from a
        // screen or the queue poll, or been replaced, since this was armed.
        if (outage !== o) return;
        clearOutage();
        if (live !== call) return;
        noteCallDiag('reconnect', {
          step: 'gave-up',
          state: call.connectionState,
          attempts: o.attempts,
          restarts: o.restarts,
          policy: call.pc.relayOnly ? 'relay' : 'all',
        });
        o.giveUp(`connection-${call.connectionState}`);
      }, CALL_RECONNECT_WINDOW_MS),
    };
    outage = o;
    noteCallDiag('reconnect', { step: 'lost', state });
  }
  const o = outage!;
  o.giveUp = giveUp;

  if (call.pc.initiatesIceRestart && o.attempts === 0) {
    if (state === 'failed') {
      // The browser has already said the old generation is gone: no grace.
      if (o.restartTimer) clearTimeout(o.restartTimer);
      attemptRestart(o);
    } else if (!o.restartTimer) {
      o.restartTimer = setTimeout(() => attemptRestart(o), CALL_RESTART_GRACE_MS);
    }
  }
  return 'wait';
}

/**
 * THE "RECOVERED" ROW — the question David's device run has to answer is
 * which path it recovered ON (direct LAN pair, or the TURN relay?). Every
 * diagnostic event row carries the stats it was taken with, `stats.path`
 * included (utils/icePath.ts via callDiag.ts), so this row says it without a
 * `getStats` of its own — and a production call pays nothing for it.
 */
function noteRecovered(call: LiveCall, o: Outage): void {
  noteCallDiag('reconnect', {
    step: 'recovered',
    attempts: o.attempts,
    restarts: o.restarts,
    policy: call.pc.relayOnly ? 'relay' : 'all',
  });
}

/**
 * HAND A LOSS IN PROGRESS TO A NEW OWNER without touching its deadline — what
 * a screen attaching or detaching does mid-loss. A no-op with no loss.
 */
function retargetReconnect(call: LiveCall, giveUp: GiveUp): void {
  if (outage && outage.call === call) outage.giveUp = giveUp;
}

/**
 * A SCREEN TOOK THIS CALL OVER. From now on restarts can run (an answer has
 * somebody to collect it), a loss in progress ends through `giveUp`, and a
 * restart that fell due while nobody was watching runs NOW — once, then the
 * ordinary retries, all on the loss's original deadline.
 */
export function attachReconnect(call: LiveCall, giveUp: GiveUp): void {
  if (live !== call) return;
  attached = call;
  retargetReconnect(call, giveUp);
  const o = outage;
  if (o && o.call === call && o.due) attemptRestart(o);
}

/**
 * THE DETACHED ENDING. `endLiveCall` does the whole teardown — it is the same
 * function the End buttons reach — and the announcement is what is extra here:
 * with no screen attached, nothing else would tell the app that the call it is
 * still advertising is over.
 */
function endDetachedCall(call: LiveCall, reason: string): void {
  if (!endLiveCall(call.sessionId)) return;
  const event = { sessionId: call.sessionId, reason };
  for (const listener of endedListeners) {
    try {
      listener(event);
    } catch {
      /* one listener's failure is not the next listener's problem */
    }
  }
}

/** The record for this session, or null — never another session's. */
export function getLiveCall(sessionId: number): LiveCall | null {
  return live && live.sessionId === sessionId ? live : null;
}

/** Whatever call is up, for callers that do not know the id. */
export function currentLiveCall(): LiveCall | null {
  return live;
}

/**
 * Take ownership of a freshly built connection. Any record for a DIFFERENT
 * session is ended first — a second call cannot start while the first is still
 * holding a microphone.
 */
export function registerLiveCall(call: LiveCall): LiveCall {
  if (live && live.sessionId !== call.sessionId) endLiveCall(live.sessionId);
  live = call;
  // Built by a stage, which is up (see `attached`).
  attached = call;
  // THE CALL BECAME LIVE, so the session is taken now — not when some screen
  // happens to mount. The release above and this acquire are a pair the audio
  // queue coalesces per key (`speakerphone`, `background`), so a hand-off
  // between two sessions settles on "held" rather than racing to "torn down".
  acquireCallAudioSession();

  return call;
}

/**
 * THE SCREEN WENT AWAY. Swap the manager's handlers for the ones that keep the
 * call alive with nobody watching: remote audio still plays, connection state
 * is still followed, outbound ICE and renegotiation still go out. Sends
 * nothing to the partner and destroys nothing.
 */
export function detachLiveCall(sessionId: number): LiveCall | null {
  const call = getLiveCall(sessionId);
  if (!call) return null;
  const ender: GiveUp = (reason) => endDetachedCall(call, reason);
  // A loss already in progress keeps its deadline; only who ends it changes.
  retargetReconnect(call, ender);
  // And it restarts nothing more until a screen is back (`attemptRestart`).
  if (attached === call) attached = null;
  call.pc.setHandlers({
    onRemoteStream: (stream) => {
      if (!call.audio) return;
      call.audio.srcObject = stream;
      void call.audio.play().catch(() => {});
    },
    /**
     * THE SAME ENDING, WITH NOBODY WATCHING. Recording the state is what this
     * used to do and all it used to do; a returning screen read it, and a call
     * that died while away simply stayed up — every device held, the pill still
     * offering "Return to call". The decision, the reconnect window and the
     * restarts are `followConnectionState`'s, so they are the stage's too.
     *
     * WHAT A DETACHED CALL DOES NOT DO is restart ICE. Inbound signalling is
     * the stage's hook, so an answer — or, on the polite side, the partner's
     * restart offer — waits on the server until a screen comes back to poll
     * it. The offerer therefore holds any restart that falls due and makes it
     * on re-attach (`attachReconnect`), inside the same window. A path that
     * heals by itself recovers here; one that needs a new ICE generation
     * recovers only if the person returns inside the window, or ends at it.
     */
    onConnectionStateChange: (state) => {
      if (followConnectionState(call, state, ender) === 'end') {
        endDetachedCall(call, `connection-${state}`);
      }
    },
    onIceCandidate: (candidate) => {
      void call.send('ice', candidate);
    },
    onRestartOffer: (offer) => {
      void call.send('offer', offer);
    },
    // Nothing is rendering a face, and the track's own mute/unmute listeners
    // were the stage's. The next attach reads the receivers afresh.
    onRemoteVideoTrack: () => {},
  });
  return call;
}

/**
 * THE PERSON ENDED THE CALL. Tell the partner, then release everything.
 *
 * `sessionId` is what the hangup is addressed to; pass `null` to tear the
 * record down without telling anybody (a reset, a sign-out, a session that was
 * never peer-to-peer). Returns whether anything was actually ended, so a
 * caller can tell a real end from a second one (class B: a `void` teardown
 * cannot say it had nothing to do).
 *
 * `sessionToken` sends the hangup under the account whose call this is, for
 * an ending that runs after that account's session already ended on screen
 * (a sign-out or a switch, see the empathy-queue store). Omitted, the hangup
 * goes out under whoever is signed in, as every in-call ending wants.
 */
export function endLiveCall(
  sessionId: number | null,
  options: { sessionToken?: string | null } = {},
): boolean {
  const call = sessionId === null ? live : getLiveCall(sessionId);
  // No record, nothing ended — and so nothing released: an End addressed to a
  // session that is not the live one must not take the live one's audio mode
  // with it (class C, the set measured is the set acted on).
  if (!call) return false;
  live = null;
  attached = null;
  // Whatever was waiting to see whether this connection came back: it did not,
  // and there is no longer a record for it to end.
  clearOutage();
  releaseCallAudioSession();

  if (sessionId !== null) {
    // Fire-and-forget: the partner's screen must not wait on our teardown, and
    // our teardown must not wait on the network.
    const url = `/empathy/signal/${sessionId}/send`;
    const hangup = { type: 'hangup', payload: {} };
    void (
      'sessionToken' in options
        ? api.post(url, hangup, { sessionToken: options.sessionToken })
        : api.post(url, hangup)
    ).catch(() => {});
  }

  // `destroy()` closes the connection and stops the manager's own local
  // stream; the sink and the microphone reference are ours to release.
  call.pc.destroy();
  call.localStream?.getTracks().forEach((track) => track.stop());
  call.localStream = null;
  if (call.audio) {
    call.audio.pause();
    call.audio.srcObject = null;
    call.audio = null;
  }
  return true;
}

/** Test seam: forget the record without touching the network. */
export function resetLiveCall(): void {
  live?.pc.destroy();
  live?.localStream?.getTracks().forEach((track) => track.stop());
  live = null;
  attached = null;
  clearOutage();
  releaseCallAudioSession();
}
